// The Scans page (warehouse app 0.8.0, store logins): what the scans of a
// warehouse mean, by order and one by one, the CSV of them, and the packages
// a carrier still has to take. Pure functions over GET wms/scans and
// GET wms/pickup.

import {
  STAGE_WORDS,
  resultText,
} from "../../../../Warehouse/WarehouseOrders/Components/OrderDetails/OrderView/orderScansModel";

export const STAGE_OPTIONS = [
  { value: "", label: "Every step" },
  ...["pick", "pack", "verify", "load", "deliver", "ship"].map((value) => ({
    value,
    label: STAGE_WORDS[value],
  })),
];

const ITEM_RESULTS = ["match", "verified", "over"];
export const PROBLEM_RESULTS = ["other_order", "wrong_item", "unknown"];

const pad = (value) => String(value).padStart(2, "0");

// "2026-11-02": a day in the browser's time.
export const dayText = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// The day `days` before (or after) a "2026-11-02".
export const shiftDay = (day, days) => {
  const [year, month, date] = String(day).split("-").map(Number);
  return dayText(new Date(year, month - 1, date + days));
};

// "2026-11-02 22:31:05" in the browser's time, for the CSV.
export const stampText = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${dayText(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const packageText = (row) =>
  row?.package?.no && row?.package?.of
    ? `${row.package.no} of ${row.package.of}`
    : row?.package?.no
      ? String(row.package.no)
      : "";

// What a scan meant on the Scans page: the order view's words, and for a
// label logged on its order, the order's label.
export const meaningText = (row) => resultText(row);

// The scans by order, the order scanned last first; codes Bolt doesn't know
// (no order) last. Each order: its scans (newest first), the items scanned,
// the labels scanned of its packages, carriers, steps, notes, and how many
// were scanned before or went wrong.
export function groupByOrder(scans) {
  const groups = new Map();
  (scans || []).forEach((row) => {
    const key = row?.order?.id ? `order-${row.order.id}` : "none";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        order: row?.order?.id ? row.order : null,
        scans: [],
        last: row,
        items: 0,
        packageNos: new Set(),
        looseLabels: 0,
        packages: null,
        carriers: new Set(),
        stages: new Set(),
        notes: new Set(),
        duplicates: 0,
        problems: 0,
      };
      groups.set(key, group);
    }
    group.scans.push(row);
    if (ITEM_RESULTS.includes(row.result)) group.items += 1;
    if (row.result === "label") {
      if (row.package?.no) group.packageNos.add(row.package.no);
      else group.looseLabels += 1;
      if (row.package?.of) group.packages = row.package.of;
    }
    if (row.carrier) group.carriers.add(row.carrier);
    if (row.stage) group.stages.add(row.stage);
    if (row.note) group.notes.add(row.note);
    if (row.duplicate) group.duplicates += 1;
    if (PROBLEM_RESULTS.includes(row.result)) group.problems += 1;
  });
  const list = [...groups.values()].map((group) => {
    const labels = group.packageNos.size + group.looseLabels;
    return {
      key: group.key,
      order: group.order,
      scans: group.scans,
      last: group.last,
      items: group.items,
      labels: group.packages ? Math.min(labels, group.packages) : labels,
      packages: group.packages,
      carriers: [...group.carriers],
      stages: [...group.stages],
      notes: [...group.notes],
      duplicates: group.duplicates,
      problems: group.problems,
    };
  });
  const none = list.filter((group) => !group.order);
  return [...list.filter((group) => group.order), ...none];
}

// The summary over every scan the filters match (Bolt's counts, not only the
// rows loaded).
export function totalsOf(counts) {
  const totals = { scans: 0, items: 0, labels: 0, problems: 0 };
  (counts || []).forEach(({ result, count }) => {
    const n = Number(count) || 0;
    totals.scans += n;
    if (ITEM_RESULTS.includes(result)) totals.items += n;
    if (result === "label" || result === "order") totals.labels += n;
    if (PROBLEM_RESULTS.includes(result)) totals.problems += n;
  });
  return totals;
}

export const CSV_COLUMNS = [
  "Time",
  "Scanned on the phone at",
  "Who",
  "Step",
  "What it meant",
  "Order",
  "Package",
  "Carrier",
  "Note",
  "Scanned before",
  "Code",
  "How",
];

// A spreadsheet cell: quoted when needed, and never read as a formula.
export const csvCell = (value) => {
  let text = value === null || value === undefined ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const SOURCE_WORDS = { camera: "Camera", gun: "Scan gun", typed: "Typed" };

// The scans as CSV (one row a scan), with a byte-order mark so Excel reads
// accents right.
export function csvOf(scans) {
  const rows = (scans || []).map((row) => [
    stampText(row.at),
    stampText(row.scanned_at),
    row.by?.name ?? "",
    STAGE_WORDS[row.stage] ?? row.stage ?? "",
    meaningText(row),
    row.order?.order_number ?? "",
    packageText(row),
    row.carrier ?? "",
    row.note ?? "",
    row.duplicate ? "Yes" : "",
    row.code ?? "",
    SOURCE_WORDS[row.source] ?? row.source ?? "",
  ]);
  return `﻿${[CSV_COLUMNS, ...rows]
    .map((cells) => cells.map(csvCell).join(","))
    .join("\r\n")}\r\n`;
}

// The packages not scanned yet, by order (in Bolt's order).
export function missingByOrder(pickup) {
  const groups = new Map();
  (pickup?.packages || []).forEach((item) => {
    if (item.scanned || !item.order?.id) return;
    const group = groups.get(item.order.id) || {
      order: item.order,
      packages: [],
    };
    group.packages.push(item);
    groups.set(item.order.id, group);
  });
  return [...groups.values()];
}

// "scans-MTL-2026-11-02.csv", or with the last day too.
export const csvName = (warehouse, from, to) =>
  `scans-${String(
    warehouse?.code || warehouse?.name || warehouse?.id || "warehouse",
  )
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(
      /^-+|-+$/g,
      "",
    )}-${from}${to && to !== from ? `-to-${to}` : ""}.csv`;
